import {
  createClient,
  LiveMap,
  LiveObject,
  type JsonObject,
} from '@liveblocks/client'
import { createRoomContext } from '@liveblocks/react'
import { apiClient, APIError } from '@/lib/api'
import type { Device, Connection, Zone } from '@/types/devices'

/**
 * Liveblocks integration (Plan 3).
 *
 * The client mints tokens via our backend `/api/v1/liveblocks/auth`
 * — never exposes the Liveblocks secret key to the browser. The
 * backend returns 503 until a real Liveblocks key is dropped into
 * Secrets Manager; `isLiveblocksAvailable` lets call sites skip
 * wrapping routes in `<RoomProvider>` when multiplayer isn't
 * configured yet.
 */

/**
 * Presence payload attached to every peer's websocket. Presence
 * is ephemeral — it flows through Liveblocks but isn't persisted
 * in Storage. Kept small because it's broadcast on every change.
 */
export type Presence = {
  /**
   * Cursor position in the room's coordinate space. For the
   * layout editor that's React Flow canvas coords; for the
   * sequencer it's pad grid cell indices. null when the pointer
   * has left the interactive region.
   */
  cursor: { x: number; y: number } | null
  /** Currently-selected / focused device (layout rooms). */
  focusedDeviceId: string | null
  /** Currently-focused pad index (sequencer rooms). */
  focusedPadIndex: number | null
  /** Display name (typically email) — shown in the avatar stack. */
  userName: string
  /**
   * Stable per-user accent colour, picked client-side from a palette.
   * Deterministic on userId so the same user keeps the same colour
   * across sessions.
   */
  userColor: string
}

/**
 * Layout-room Storage shape — CRDT-backed state shared across
 * every peer in a `layout:<id>` room. Serves as the authoritative
 * source for devices / connections / zones while peers are
 * collaborating; a designated "leader" peer periodically pushes
 * a snapshot to DynamoDB for durable persistence (see
 * `useMultiplayerAutoSave`).
 *
 * Entities are stored as plain JSON inside a `LiveMap` rather
 * than wrapped in individual `LiveObject`s — every mutation that
 * touches an entity replaces the whole record. This keeps the
 * CRDT semantics simple (last-write-wins per-entity) at the cost
 * of deeper granularity (if two peers tweak different fields of
 * the same device simultaneously, the later write wins rather
 * than merging). Acceptable for the layout editor where
 * simultaneous edits of the same device are rare; if that
 * changes we can switch to `LiveObject<Device>` per entry.
 *
 * Project rooms currently share the same Storage type. The
 * sequencer doesn't yet read or write Storage — its data still
 * flows through the REST API from Plan 2b. When the sequencer
 * migrates it gets a separate `ProjectStorage` type.
 */
export type Storage = {
  devices: LiveMap<string, JsonObject>
  connections: LiveMap<string, JsonObject>
  zones: LiveMap<string, JsonObject>
  meta: LiveObject<{ name: string; version: number }>
}

/**
 * App-side Storage entity shapes. The Liveblocks SDK requires
 * stored values to satisfy `Lson` (`JsonObject` at this shape),
 * which would force every field on Device / Connection / Zone to
 * accept an index signature. Rather than polluting those app
 * types with `[key: string]: unknown`, we erase the type at the
 * Storage boundary and restore it via these casts at the single
 * read/write sites in `useMultiplayerLayoutSync`. Helper types
 * so that boundary is explicit rather than spray-cast via `any`.
 */
export type DeviceJson = Device & JsonObject
export type ConnectionJson = Connection & JsonObject
export type ZoneJson = Zone & JsonObject

/**
 * Initial Storage shape handed to `<RoomProvider initialStorage>`.
 * Liveblocks only applies this if the room has no existing
 * Storage — subsequent loads use whatever peers have already
 * written. The first peer to open a layout room seeds Storage
 * from the REST snapshot they fetched (see
 * `useMultiplayerLayoutSync`).
 */
export function emptyLayoutStorage(): Storage {
  return {
    devices: new LiveMap<string, JsonObject>(),
    connections: new LiveMap<string, JsonObject>(),
    zones: new LiveMap<string, JsonObject>(),
    meta: new LiveObject<{ name: string; version: number }>({
      name: '',
      version: 0,
    }),
  }
}

export type UserMeta = {
  id: string
  info: {
    name: string
    tier?: string
  }
}

const client = createClient({
  authEndpoint: async room => {
    try {
      const res = await apiClient.post<{ token: string }>(
        '/api/v1/liveblocks/auth',
        { room },
      )
      // Defensive: log if the backend ever returns a 200 without a
      // `token` field (would be a contract break, but we want a
      // crisp signal instead of the SDK's generic "callback should
      // return a token" error).
      if (!res || typeof res.token !== 'string') {
        console.error('[liveblocks-auth] backend returned no token', { res })
      }
      return res
    } catch (err) {
      // The Liveblocks SDK accepts `{ error, reason }` as a return
      // value to signal "auth refused, don't retry". Without this,
      // any non-2xx response from our API surfaces in the console
      // as the unhelpful "callback should return a token" error.
      // Log the real cause first so the iPad / Safari Web Inspector
      // shows what actually went wrong.
      if (err instanceof APIError) {
        console.error('[liveblocks-auth] failed', {
          room,
          status: err.status,
          code: err.code,
          message: err.message,
          body: err.body,
        })
        if (err.status === 503) {
          // Multiplayer not configured — degrade silently.
          return { error: 'forbidden', reason: 'Multiplayer not configured' }
        }
        if (err.status === 401) {
          return { error: 'forbidden', reason: 'Not authenticated' }
        }
        if (err.status === 403 || err.status === 404) {
          return { error: 'forbidden', reason: err.message ?? 'Access denied' }
        }
      } else {
        console.error('[liveblocks-auth] unexpected failure', { room, err })
      }
      throw err
    }
  },
  // Low throttle to keep presence updates smooth (cursor follows);
  // Liveblocks defaults to 100ms which is already fine.
  throttle: 50,
})

export const {
  RoomProvider,
  useRoom,
  useMyPresence,
  useOthers,
  useOthersConnectionIds,
  useOther,
  useStorage,
  useMutation,
  useSelf,
  useStatus,
} = createRoomContext<Presence, Storage, UserMeta>(client)

/**
 * Deterministic pastel colour from a userId. Keeps each peer's
 * ring / cursor stable across sessions so people start to recognise
 * "that's Daisy's green cursor". 10-colour palette — balance
 * between variety and avoiding near-duplicates.
 */
const PRESENCE_PALETTE = [
  '#F87171', // red
  '#FB923C', // orange
  '#FBBF24', // amber
  '#A3E635', // lime
  '#34D399', // emerald
  '#22D3EE', // cyan
  '#60A5FA', // blue
  '#818CF8', // indigo
  '#C084FC', // purple
  '#F472B6', // pink
]

export function pickPresenceColor(userId: string): string {
  // Simple hash — sum of char codes mod palette size. Collisions
  // are fine; this is an identity cue, not a primary key.
  let h = 0
  for (let i = 0; i < userId.length; i++) h = (h + userId.charCodeAt(i)) | 0
  const idx = Math.abs(h) % PRESENCE_PALETTE.length
  return PRESENCE_PALETTE[idx]
}

/**
 * Known room kinds. Callers build the room id from these.
 */
export function layoutRoomId(layoutId: string): string {
  return `layout:${layoutId}`
}
export function projectRoomId(projectId: string): string {
  return `project:${projectId}`
}

/**
 * Tentative feature flag — multiplayer UI is gated on the caller
 * having (a) a paid tier with `teamSharing` enabled and (b) the
 * runtime environment having Liveblocks configured server-side.
 * The server-side check is implicit: the `authEndpoint` call
 * returns 503 and the RoomProvider disables itself.
 *
 * For now, just a lightweight helper — we don't eagerly ping the
 * auth endpoint, so `true` here means "the UI path can wrap this
 * route in a RoomProvider". If the backend isn't configured the
 * Provider still mounts but enters an `initial` status and never
 * progresses.
 */
export function isLiveblocksEligible(
  features: { teamSharing?: boolean } | null | undefined,
): boolean {
  return Boolean(features?.teamSharing)
}
